/* eslint-disable func-style */
import { useCallback, useEffect, useRef } from 'react';

export function useDebouncedCallback<T extends (...args: unknown[]) => void>(
  fn: T,
  delay: number,
  deps: unknown[] = []
) {
  const fnRef = useRef(fn);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  const cancel = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const debounced = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(() => {
        fnRef.current(...args);
        timeoutRef.current = null;
      }, delay);
    },
    [delay]
  );

  const flush = useCallback(
    (...args: Parameters<T>) => {
      cancel();
      fnRef.current(...args);
    },
    [cancel]
  );

  useEffect(() => {
    cancel();
  }, [cancel, ...deps]);

  return { debounced, cancel, flush } as const;
}
